import { QUALITY_TIERS } from "../render/quality";
import { GRASS_LEVELS, GraphicsPrefs, GraphicsPrefsData, RESOLUTIONS, effectiveTier } from "../render/graphicsPrefs";
import { btnHtml } from "./kit";
import { sheetHeadHtml } from "./components";
import { paintedIconHtml } from "./featureIcons";
import { reducedVfx } from "./settings";

/** Settings' painted cog in the header's portrait tile (the header's art size, components.css .k-head). */
const GRAPHICS_PORTRAIT_PX = 52;

/**
 * Settings → Graphics: a quality preset, then each costly feature on its own, remembered on this device. A row the
 * player has changed shows a dot; RESET goes back to the preset. Every choice applies at once except the ground
 * scatter, which is laid out when a level is built. Glow and ambient occlusion are forced off under Settings → Reduced
 * effects (render/quality.ts postPlan, SceneRig.aoAllowed), so there those rows show Off, locked, and say why.
 */
export function showGraphics(parent: HTMLElement): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel settings graphics";
  const seg = <T>(key: keyof GraphicsPrefsData, options: readonly { label: string; value: T }[], current: T, forced = false) =>
    `<div class="seg${forced ? " forced" : ""}" data-key="${key}">${options.map((o, i) => `<button class="${o.value === current ? "on" : ""}" data-i="${i}"${forced ? " disabled" : ""}>${o.label}</button>`).join("")}</div>`;
  const row = (key: keyof GraphicsPrefsData | "", title: string, sub: string, control: string) => {
    const changed = key !== "" && GraphicsPrefs.current[key] !== null;
    return `<div class="srow"><div class="lbl"><b>${title}${changed ? ' <i class="dot" title="changed from preset"></i>' : ""}</b>${sub ? `<small>${sub}</small>` : ""}</div>${control}</div>`;
  };
  const onOff = [{ label: "Off", value: false }, { label: "On", value: true }];
  const choices: Partial<Record<keyof GraphicsPrefsData, readonly { label: string; value: unknown }[]>> = {};
  const render = () => {
    const presetIndex = GraphicsPrefs.presetIndex();
    const recommended = GraphicsPrefs.recommended, recIndex = GraphicsPrefs.recommendedIndex();
    const t = effectiveTier(QUALITY_TIERS[presetIndex]);
    const dpr = window.devicePixelRatio || 1;
    const res = RESOLUTIONS.filter((r) => r <= dpr + 1e-3).map((r) => ({ label: `${r}×`, value: r }));
    const shadows = [{ label: "Off", value: "off" }, { label: "Hard", value: "hard" }, { label: "Soft", value: "soft" }];
    const grass = GRASS_LEVELS;
    Object.assign(choices, { resolution: res, shadows, bloom: onOff, ao: onOff, msaa: onOff, grass });
    const shadowNow = !t.shadows ? "off" : t.softShadows ? "soft" : "hard";
    const grassNow = GraphicsPrefs.current.grass ?? 1;
    const reduced = reducedVfx();
    const forcedNote = "Off while Reduced effects is on";
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Graphics", portrait: paintedIconHtml("settings", GRAPHICS_PORTRAIT_PX) })}
      <div class="gbody">
      <div class="srow preset"><div class="lbl"><b>Quality preset</b>${GraphicsPrefs.customised && !recommended ? `<small>Customised</small>` : ""}</div></div>
      <div class="seg rec"><button class="${recommended ? "on" : ""}" data-rec="1">Recommended <span class="tiername">· ${QUALITY_TIERS[recIndex].name}</span></button></div>
      <div class="seg presets">${QUALITY_TIERS.map((q, i) => `<button class="${i === presetIndex && !GraphicsPrefs.customised && !recommended ? "on" : i === presetIndex ? "on dim" : ""}" data-p="${i}">${q.name}</button>`).join("")}</div>
      <div class="scard">
      ${row("resolution", "Resolution", "", seg("resolution", res, nearestOption(res.map((r) => r.value), Math.min(t.pixelRatio, dpr))))}
      ${row("shadows", "Shadows", "", seg("shadows", shadows, shadowNow))}
      ${row("bloom", "Glow", reduced ? forcedNote : "", seg("bloom", onOff, t.bloom > 0 && !reduced, reduced))}
      ${row("ao", "Ambient occlusion", reduced ? forcedNote : "", seg("ao", onOff, t.ao && !reduced, reduced))}
      ${row("msaa", "Anti-aliasing", "", seg("msaa", onOff, t.msaa > 0))}
      ${row("grass", "Grass and flowers", "", seg("grass", grass, grassNow))}
      </div>
      </div>
      <div class="row two">${btnHtml("RESET", "dark", { className: "greset disp", disabled: !GraphicsPrefs.customised })}${btnHtml("DONE", "gold", { className: "gdone disp" })}</div>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".gdone")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".greset")!.onclick = () => { GraphicsPrefs.reset(); render(); };
    sheet.querySelector<HTMLButtonElement>(".seg.rec button")!.onclick = () => { GraphicsPrefs.chooseRecommended(); render(); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".presets button")) b.onclick = () => { GraphicsPrefs.choosePreset(Number(b.dataset.p)); render(); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".seg[data-key] button")) {
      b.onclick = () => {
        const key = b.closest<HTMLElement>(".seg")!.dataset.key as keyof GraphicsPrefsData;
        const opt = choices[key]![Number(b.dataset.i)];
        GraphicsPrefs.set({ [key]: opt.value } as Partial<GraphicsPrefsData>);
        render();
      };
    }
  };
  render();
  parent.append(sheet);
  return sheet;
}

/**
 * The offered value nearest to `v`: a tier's resolution need not be one of the choices (medium draws at 1.25×, lowest
 * at 0.8×), and a row with nothing lit reads as broken. Ties go to the lower value. Display only; the tier is unchanged.
 */
export function nearestOption(values: readonly number[], v: number): number {
  let best = values[0] ?? v;
  for (const x of values) if (Math.abs(x - v) < Math.abs(best - v)) best = x;
  return best;
}
